import dayjs from 'dayjs'
import i18n from 'i18n'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import Database from 'services/db/Database'
import { eventsForReminders } from 'services/db/Queries/Event'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'

import { isTestEnv } from 'utils/env'

import { isCypress } from 'utils/cypress'
import { getDefaultTimezone } from 'index'
import LocalStorage from 'services/LocalStorage'
import { generateGroupcalArrayFromRecurrence } from 'utils/recurrence'
import Snooze, { ISnooze } from 'model/models/Snooze'
import { allSnooze } from 'services/db/Queries/SnoozeQuieries'
import Logger from 'services/Logger'
import { CollectionType } from 'types/model'
import { is24meApp } from 'utils/appType'

export function MinuteTimer({ children }: { children: React.ReactNode }) {
  const [currentTime, setCurrentTime] = useState<Date>(new Date())
  const [timerId, setTimerId] = useState<number | any | undefined>(undefined)

  function showEventAlertsIfNeeded() {
    const now = dayjs()

    try {
      allSnooze().then((snoozes) => {
        ;(snoozes ?? []).forEach((snooze) => {
          if (snooze.fireAt) {
            const fireAt = dayjs(snooze.fireAt)
            if (fireAt.diff(now, 'minute') === 0) {
              Logger.orange('Showing snoozed notif', snooze)
              showNotification(`${snooze.title}`, snooze.body, snooze.tag)
              Database.write(async () => {
                await snooze.markAsDeleted()
              })
            }
          }
        })
      })

      UserSettings().then((userSettings) => {
        eventsForReminders(userSettings).then((events) => {
          events.forEach((groupcalEvent) => {
            const instances =
              groupcalEvent.Recurrence && groupcalEvent.Recurrence.Unit
                ? generateGroupcalArrayFromRecurrence(
                    groupcalEvent,
                    false,
                    dayjs().startOf('day'),
                    dayjs().add(7, 'days').endOf('day'),
                    getDefaultTimezone()
                  )
                : [groupcalEvent]
            //going over all reminders

            if (
              groupcalEvent?.Reminder &&
              typeof groupcalEvent?.Reminder !== 'string'
            ) {
              groupcalEvent?.Reminder?.forEach((reminder) => {
                //per each instance
                instances.forEach((instance) => {
                  const parentStart = dayjs(
                    Number(groupcalEvent.StartDate) * 1000
                  )
                  let eventStart = dayjs
                    .unix(Number(instance.StartDate))
                    .set('hour', parentStart.hour())

                  if (instance.AllDay === '1') {
                    eventStart = dayjs
                      .unix(Number(instance.StartDate))
                      .tz('UTC')
                  }

                  const eventEnd = dayjs.unix(Number(instance.EndDate))
                  const minutesDiff = eventStart.diff(
                    now.tz(
                      instance.AllDay === '1' ? 'UTC' : getDefaultTimezone()
                    ),
                    'minute'
                  )

                  const isAbsolute = reminder.isRelativeReminder === '0'

                  // Absolute reminders: fire exactly at AlertTime
                  if (isAbsolute && reminder.AlertTime) {
                    const alertTime = dayjs.unix(Number(reminder.AlertTime))
                    const alertDiff = alertTime.diff(now, 'minute')

                    if (alertDiff === 0) {
                      Logger.debug(
                        'will present notification for (absolute)',
                        groupcalEvent
                      )
                      const startText = eventStart.format('HH:mm')
                      const endText = eventEnd.format('HH:mm')

                      let secondLine = `${startText} - ${endText}`

                      if (groupcalEvent.AllDay === '1') {
                        secondLine = `${eventStart.format('MMM DD')}, ${t(
                          'allDay'
                        )}`
                      }

                      if (startText === endText) {
                        secondLine = `${startText}`
                      }

                      if (groupcalEvent.Location?.Address) {
                        secondLine += `\n${groupcalEvent.Location.Address}`
                      }
                      if (isTestEnv()) {
                        secondLine += `\n${process.env.REACT_APP_LINK_DOMAIN}`
                      }
                      showNotification(
                        groupcalEvent.Text ?? '',
                        secondLine,
                        groupcalEvent.id
                      )
                    }
                  }

                  // Relative reminders: existing logic
                  if (
                    !isAbsolute &&
                    ((reminder.offset === 0 && minutesDiff === 0) ||
                      (reminder.offset != 0 &&
                        minutesDiff === reminder.offset / 60 - 1))
                  ) {
                    Logger.debug(
                      'will present notification for ',
                      groupcalEvent
                    )
                    const startText = eventStart.format('HH:mm')
                    const endText = eventEnd.format('HH:mm')

                    let secondLine = `${startText} - ${endText}`

                    if (groupcalEvent.AllDay === '1') {
                      secondLine = `${eventStart.format('MMM DD')}, ${t(
                        'allDay'
                      )}`
                    }

                    if (startText === endText) {
                      secondLine = `${startText}`
                    }

                    if (groupcalEvent.Location?.Address) {
                      secondLine += `\n${groupcalEvent.Location.Address}`
                    }
                    if (isTestEnv()) {
                      secondLine += `\n${process.env.REACT_APP_LINK_DOMAIN}`
                    }
                    showNotification(
                      groupcalEvent.Text ?? '',
                      secondLine,
                      groupcalEvent.id
                    )
                  }
                })
              })
            }
          })
        })
      })
    } catch (e) {
      //ignore minute update errors per https://24meapp.atlassian.net/jira/software/c/projects/SER/boards/15?view=detail&selectedIssue=SER-1308
    }
  }

  function launchEveryMinute() {
    Logger.pink('minute tick')
    try {
      showEventAlertsIfNeeded()
    } catch (e) {
      Logger.red('minute', e)
    }
  }

  useEffect(() => {
    // Check if service worker is available
    if (!('serviceWorker' in navigator)) {
      Logger.red('Service worker not supported')
      return
    }

    const handleMessage = (event: MessageEvent) => {
      console.log('Received message from service worker:', event)

      // Make sure this is a notification click event
      if (event.data && event.data.type === 'NotificationClick') {
        const snoozeData: ISnooze = event.data.notification.data
        Logger.orange('Received snooze data', snoozeData)

        Database.write(async () => {
          Database.getCollection<Snooze>(CollectionType.Snooze).create(
            (snooze) => {
              snooze.tag = snoozeData.tag
              snooze.title = snoozeData.title
              snooze.body = snoozeData.body
              snooze._raw['fireAt'] = snoozeData.fireAt
            }
          )
        })
      }
    }

    // Add the event listener
    navigator.serviceWorker.addEventListener('message', handleMessage)

    // Cleanup function to remove the listener
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage)
    }
  }, [])

  useEffect(() => {
    const storedTimerId = LocalStorage.get('timerFId')
    if (storedTimerId) {
      if (isCypress()) return
      setTimerId(parseInt(storedTimerId, 10))
    }

    return () => {
      if (timerId) {
        clearInterval(timerId)
        LocalStorage.remove('timerId')
      }
    }
  }, [timerId])

  useEffect(() => {
    if (!timerId) {
      const msUntilNextMinute =
        (60 - currentTime.getSeconds()) * 1000 - currentTime.getMilliseconds()

      const newTimer = setInterval(() => {
        if (isCypress()) return
        setCurrentTime(new Date())
      }, 60 * 1000)

      if (isCypress()) return
      setTimerId(newTimer)
      localStorage.setItem('timerId', newTimer.toString())

      setTimeout(() => {
        if (isCypress()) return
        setCurrentTime(new Date())
        clearInterval(newTimer)
        localStorage.removeItem('timerId')

        const nextTimer = setInterval(() => {
          if (isCypress()) return
          setCurrentTime(new Date())
          launchEveryMinute()
        }, 60 * 1000)

        if (isCypress()) return
        setTimerId(nextTimer)
        localStorage.setItem('timerId', nextTimer.toString())
        launchEveryMinute()
      }, msUntilNextMinute)
      launchEveryMinute()
    }
  }, [currentTime, timerId])

  return <>{children}</>
}

const humanizeDuration = require('humanize-duration')

interface SnoozeButton {
  action: string
  title: string
}

const snoozeButtons: SnoozeButton[] = [
  {
    action: 'snooze1',
    title: t('snoozeFor', {
      timeSpan: humanizeDuration(1 * 60 * 1000, {
        language:
          i18n.language.length > 2 ? i18n.language.slice(0, 2) : i18n.language
      })
    })
  },
  {
    action: 'snooze5',
    title: t('snoozeFor', {
      timeSpan: humanizeDuration(5 * 60 * 1000, {
        language:
          i18n.language.length > 2 ? i18n.language.slice(0, 2) : i18n.language
      })
    })
  },
  {
    action: 'snooze15',
    title: t('snoozeFor', {
      timeSpan: humanizeDuration(15 * 60 * 1000, {
        language:
          i18n.language.length > 2 ? i18n.language.slice(0, 2) : i18n.language
      })
    })
  },
  {
    action: 'snooze60',
    title: t('snoozeFor', {
      timeSpan: humanizeDuration(60 * 60 * 1000, {
        language:
          i18n.language.length > 2 ? i18n.language.slice(0, 2) : i18n.language
      })
    })
  }
]

export function showNotification(title: string, body: string, tag: string) {
  if (typeof Notification === 'undefined') return
  try {
    Logger.blue('notif state', Notification.permission)
    const options: NotificationOptions & {
      actions?: { action: string; title: string }[]
    } = {
      body: body,
      icon: is24meApp() ? '/24me_192x192.png' : '/logo192.png',
      requireInteraction: true,
      tag: tag,
      actions: snoozeButtons.map(({ action, title }) => ({ action, title })),
      data: {
        body: body,
        tag: tag,
        title: title
      }
    }
    if (Notification.permission === 'granted') {
      // showNotif(title, options)

      navigator.serviceWorker.getRegistration().then(function (reg) {
        reg?.showNotification(title, options)
      })
    } else if (Notification.permission !== 'denied') {
      Promise.resolve(Notification.requestPermission()).then(
        function (permission) {
          if (permission === 'granted') {
            // showNotif(title, options)
            navigator.serviceWorker.getRegistration().then(function (reg) {
              reg?.showNotification(title, options)
            })
          } else {
            alert(title)
          }
        }
      )
    }
  } catch (e) {
    console.error('no way to show notif', e)
  }
}

export function showNotif(title: string, options: NotificationOptions) {
  if ('serviceWorker' in navigator && 'ServiceWorkerRegistration' in window) {
    navigator.serviceWorker.ready
      .then((registration) => {
        Logger.debug('presenting notif with options', options)
        registration.showNotification(title, options)
        registration.addEventListener('notificationclick', (e) => {
          Logger.debug('clicked on a notif', e)
        })
      })
      .catch((error) => {
        console.log('Service worker registration failed:', error)
      })
  }
}
